import type { Component } from "vue";
import type { RouteRecordRaw } from "vue-router";
import type { OptionsDropdownOption } from "@/components/features/OptionsDropdown";
import { nextTick } from "vue";
import { createRouter, createWebHistory } from "vue-router";

import { $t, setLocale } from "@/services/i18n";
import { useAuth } from "@/stores/auth.store";
import { hasAnyPermission, hasOrganizationScope, hasPermission } from "@/stores/user-profile.store";
import { getChargeSubtypeDesign } from "@/utils/charge-types";
import { getLoginDestination } from "@/utils/navigation";
import {
  createAgentActions,
  createAgentPageSelector,
  createPlaygroundPageSelector,
} from "./breadcrumb-utils";
import {
  createAgentDropdownLabel,
  createApiKeyDropdownLabel,
  createKnowledgeDropdownLabel,
  createWebhookDropdownLabel,
} from "./utils";

export type RouteMetaLabel =
  | string
  | {
      options: {
        label: string | (() => Component);
        value: string;
        textValue: string;
      }[];
      value: string;
      key: string;
    };

declare module "vue-router" {
  interface RouteMeta {
    translationKey?: string;
    icon?: string;
    requiresAuth?: boolean;
    group?: string;
    navigation?: "menu" | "dropdown";
    navigationGroup?: "project" | "organization";
    parent?: string | ((params: Record<string, any>) => RouteRecordRaw);
    searchTerm?: string;
    subMenu?: string;
    condition?: () => boolean | Promise<boolean>;
    feature?: string;
    isBeta?: () => boolean;
    isDeprecated?: () => boolean;
    paramChild?: { label: string; param: Record<string, string> }[];
    label?: (params: Record<string, any>) => RouteMetaLabel;
    defaultParams?: Record<string, string>;
    optionsDropdown?: (payload: {
      route: RouteLocationNormalizedLoaded;
      router: Router;
    }) => OptionsDropdownOption[];
    /**
     * When true, switching projects will show a confirmation dialog
     * before navigating away. Switching organizations always leaves the
     * route for its declared parent because its entity IDs belong to the
     * previous organization. Used for entity, edit, and create pages.
     */
    projectSwitchProtection?: boolean;
    /**
     * Sort order for sidebar navigation items within a group.
     * Lower numbers appear first. Default is 999.
     */
    sortOrder?: number;
    // Sistema de breadcrumb verdadeiramente declarativo
    breadcrumb?: Array<{
      type: "entity-selector" | "page-selector" | "static-page";
      label?: (params: Record<string, any>) => RouteMetaLabel | string;
      options?: (params: Record<string, any>) => Array<{
        value: string;
        label: string;
        route?: any;
      }>;
      actions?: (payload: {
        route: RouteLocationNormalizedLoaded;
        router: Router;
      }) => OptionsDropdownOption[];
      condition?: (params: Record<string, any>) => boolean;
    }>;
  }
}

const routes = [
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("@/pages/dashboard/Dashboard"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: "lucide:bar-chart-2",
      translationKey: "dashboard",
      condition: () => hasPermission("dashboard:read"),
    },
  },
  {
    path: "/dashboard/agents",
    name: "AgentsDashboard",
    component: () => import("@/pages/dashboard/agents/AgentsDashboard"),
    meta: {
      parent: "Dashboard",
      translationKey: "agents",
      icon: "lucide:users",
      condition: () => hasPermission("agents:read"),
    },
  },
  {
    path: "/usage-exports/:id/download",
    name: "UsageExportDownload",
    component: () => import("@/pages/dashboard/UsageExportDownload"),
    meta: {
      parent: "Dashboard",
      condition: () => hasPermission("dashboard:read") && hasPermission("billing:read"),
    },
  },
  {
    path: "/playground",
    name: "Playground",
    redirect: "/playground/text-generation",
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: "lucide:terminal",
      translationKey: "playground",
      condition: () =>
        hasAnyPermission("llm:execute", "asr:execute", "tts:execute", "anonymization:execute", "decisions:execute"),
    },
  },

  {
    path: "/playground/text-generation",
    name: "Text Generation",
    component: () => import("@/pages/playground/llm/LLM"),
    meta: {
      subMenu: "Playground",
      parent: "Playground",
      translationKey: "text_generation",
      icon: getChargeSubtypeDesign("text-generation")?.icon,
      condition: () => hasPermission("llm:execute"),
      breadcrumb: [
        {
          type: "page-selector",
          options: () => createPlaygroundPageSelector().getOptions(),
        },
      ],
    },
  },
  {
    path: "/playground/speech-to-text",
    name: "Speech to Text",
    component: () => import("@/pages/playground/asr/ASR"),
    meta: {
      subMenu: "Playground",
      parent: "Playground",
      translationKey: "speech_to_text",
      icon: getChargeSubtypeDesign("speech-to-text")?.icon,
      condition: () => hasPermission("asr:execute"),
      breadcrumb: [
        {
          type: "page-selector",
          options: () => createPlaygroundPageSelector().getOptions(),
        },
      ],
    },
  },

  {
    path: "/playground/text-to-speech",
    name: "Text to Speech",
    component: () => import("@/pages/playground/tts/TTS"),
    meta: {
      subMenu: "Playground",
      parent: "Playground",
      translationKey: "text_to_speech",
      icon: getChargeSubtypeDesign("text-to-speech")?.icon,
      condition: () => hasPermission("tts:execute"),
      breadcrumb: [
        {
          type: "page-selector",
          options: () => createPlaygroundPageSelector().getOptions(),
        },
      ],
    },
  },
  {
    path: "/playground/anonymization",
    name: "Anonymization",
    component: () => import("@/pages/playground/anonymization/Anonymization"),
    meta: {
      subMenu: "Playground",
      parent: "Playground",
      translationKey: "anonymization",
      icon: getChargeSubtypeDesign("anonymization")?.icon,
      condition: () => hasPermission("anonymization:execute"),
      breadcrumb: [
        {
          type: "page-selector",
          options: () => createPlaygroundPageSelector().getOptions(),
        },
      ],
    },
  },
  {
    path: "/playground/audio-enhancement",
    name: "Audio Enhancement",
    component: () => import("@/pages/playground/audio-enhancement/AudioEnhancement"),
    meta: {
      subMenu: "Playground",
      parent: "Playground",
      translationKey: "audio_enhancement",
      icon: getChargeSubtypeDesign("audio-enhancement")?.icon,
      condition: () => false,
      breadcrumb: [
        {
          type: "page-selector",
          options: () => createPlaygroundPageSelector().getOptions(),
        },
      ],
    },
  },

  {
    path: "/playground/decisions",
    name: "Decisions",
    component: () => import("@/pages/playground/decisions/Decisions"),
    meta: {
      subMenu: "Playground",
      parent: "Playground",
      translationKey: "decisions",
      icon: getChargeSubtypeDesign("decision")?.icon,
      condition: () => hasPermission("decisions:execute"),
      breadcrumb: [
        {
          type: "page-selector",
          options: () => createPlaygroundPageSelector().getOptions(),
        },
      ],
    },
  },

  {
    path: "/knowledge",
    name: "Knowledge",
    component: () => import("@/pages/knowledge/KnowledgeBase"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: "lucide:database",
      translationKey: "knowledge",
      condition: () => hasPermission("knowledge:read"),
    },
  },
  {
    path: "/knowledge/:id",
    name: "Edit Knowledge",
    component: () => import("@/pages/knowledge/Knowledge"),
    meta: {
      parent: "Knowledge",
      condition: () => hasPermission("knowledge:read"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "entity-selector",
          label: createKnowledgeDropdownLabel,
        },
      ],
    },
  },

  {
    path: "/agents",
    name: "Agents",
    component: () => import("@/pages/agents/core/Agents"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: getChargeSubtypeDesign("agent")?.icon,
      translationKey: "agents",
      condition: () => hasPermission("agents:read"),
    },
  },
  {
    path: "/agents/create-agent/:tab",
    name: "Create Agent",
    component: () => import("@/pages/agents/form/CreateAgent"),
    meta: {
      icon: getChargeSubtypeDesign("agent")?.icon,
      parent: "Agents",
      translationKey: "create_agent",
      condition: () => hasPermission("agents:create"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "static-page",
          label: () => $t("agents:create_agent"),
        },
      ],
    },
  },

  {
    path: "/agents/edit-agent/:agent_id/:tab",
    name: "Edit Agent",
    component: () => import("@/pages/agents/form/EditAgent"),
    meta: {
      parent: "Agents",
      defaultParams: { tab: "profile" },
      translationKey: "edit_agent",
      label: createAgentDropdownLabel,
      condition: () => hasPermission("agents:read"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "entity-selector",
          label: createAgentDropdownLabel,
          actions: createAgentActions(),
        },
        {
          type: "page-selector",
          options: (params) => createAgentPageSelector().getOptions(params),
          actions: createAgentActions(),
        },
      ],
    },
  },
  {
    path: "/agents/deploy/:agent_id",
    name: "Agent Deploy",
    component: () => import("@/pages/agents/deploy/AgentDeployPage"),
    meta: {
      parent: "Agents",
      translationKey: "common:deployments",
      label: createAgentDropdownLabel,
      condition: () => hasPermission("agents:read"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "entity-selector",
          label: createAgentDropdownLabel,
          actions: createAgentActions(),
        },
        {
          type: "page-selector",
          options: (params) => createAgentPageSelector().getOptions(params),
          actions: createAgentActions(),
        },
      ],
    },
  },
  {
    path: `/agents/:agent_id/:thread_id`,
    name: "Agent Playground",
    component: () => import("@/pages/agents/threads/AgentThreads"),
    meta: {
      parent: "Agents",
      defaultParams: {
        thread_id: "new",
      },
      label: createAgentDropdownLabel,
      condition: () => hasPermission("threads:read"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "entity-selector",
          label: createAgentDropdownLabel,
          actions: createAgentActions(),
        },
        {
          type: "page-selector",
          options: (params) => createAgentPageSelector().getOptions(params),
          actions: createAgentActions(),
        },
      ],
    },
  },

  {
    path: "/agents/:agent_id/voice",
    redirect: (to) => ({
      name: "Agent Playground",
      params: { agent_id: to.params.agent_id, thread_id: "new" },
    }),
  },

  {
    path: "/agents/:agent_id/conversations/:thread_id?",
    name: "Agent Conversations",
    component: () => import("@/pages/agents/conversations/AgentConversations"),
    meta: {
      parent: "Agents",
      label: createAgentDropdownLabel,
      condition: () => hasPermission("threads:read"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "entity-selector",
          label: createAgentDropdownLabel,
          actions: createAgentActions(),
        },
        {
          type: "page-selector",
          options: (params) => createAgentPageSelector().getOptions(params),
          actions: createAgentActions(),
        },
      ],
    },
  },

  {
    path: "/structured-analysis",
    name: "Structured Analysis",
    component: () => import("@/pages/structured-analysis/StructuredAnalysis"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: "lucide:lightbulb",
      translationKey: "structured_analysis",
      condition: () => hasPermission("structured_analysis:read"),
    },
  },

  {
    path: "/team",
    name: "Team",
    component: () => import("@/pages/team/TeamPage"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      sortOrder: 1000,
      icon: "lucide:users-round",
      translationKey: "team",
      condition: () => hasPermission("team:read"),
    },
  },
  {
    path: "/keys",
    name: "Api Keys",
    component: () => import("@/pages/api-keys/ApiKeys"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: "lucide:key",
      translationKey: "api_keys",
      condition: () => hasAnyPermission("api_keys:read", "api_keys:read:own"),
    },
  },

  {
    path: "/keys/:id/usage",
    name: "Api Key Usage",
    component: () => import("@/pages/api-keys/ApiKeyUsage"),
    meta: {
      translationKey: "api_key_usage",
      parent: "Api Keys",
      condition: () =>
        hasAnyPermission("api_keys:read", "api_keys:read:own") && hasPermission("dashboard:read"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "entity-selector",
          label: createApiKeyDropdownLabel,
        },
      ],
    },
  },

  {
    path: "/billing",
    name: "Billing",
    redirect: "/billing/overview",
    meta: {
      navigation: "menu",
      navigationGroup: "organization",
      sortOrder: 5,
      icon: "lucide:receipt",
      translationKey: "billing",
      condition: () => hasPermission("billing:read"),
    },
  },
  {
    path: "/billing/:tab",
    name: "BillingTab",
    component: () => import("@/pages/billing/Billing"),
    meta: {
      translationKey: "billing",
      condition: () => hasPermission("billing:read"),
    },
  },

  {
    path: "/organization/analytics",
    name: "OrganizationAnalytics",
    component: () => import("@/pages/organization/OrganizationAnalytics"),
    meta: {
      navigation: "menu",
      navigationGroup: "organization",
      sortOrder: 0,
      icon: "lucide:chart-area",
      translationKey: "analytics",
      condition: () => hasPermission("dashboard:read"),
    },
  },
  {
    path: "/integrations",
    name: "Integrations",
    component: () => import("@/pages/integrations/Integrations"),
    meta: {
      navigation: "menu",
      navigationGroup: "organization",
      sortOrder: 2,
      icon: "lucide:key-round",
      translationKey: "integrations",
      condition: () => hasPermission("credentials:read"),
    },
  },
  {
    path: "/members",
    name: "Members",
    component: () => import("@/pages/members/Members"),
    meta: {
      navigation: "menu",
      navigationGroup: "organization",
      sortOrder: 1,
      icon: "lucide:user-cog",
      translationKey: "members",
      condition: () => hasPermission("members:read"),
    },
  },
  {
    path: "/organization/plan",
    name: "OrganizationPlan",
    component: () => import("@/pages/organization/OrganizationPlan"),
    meta: {
      navigation: "menu",
      navigationGroup: "organization",
      sortOrder: 4,
      icon: "lucide:crown",
      translationKey: "organization_plan",
      condition: () => hasPermission("billing:read"),
    },
  },
  {
    path: "/organization/numbers",
    name: "OrganizationNumbers",
    component: () => import("@/pages/organization/numbers/OrganizationNumbers"),
    meta: {
      navigation: "menu",
      navigationGroup: "organization",
      sortOrder: 3,
      icon: "lucide:phone",
      translationKey: "phone_numbers",
      condition: () => hasPermission("dids:manage") && hasOrganizationScope(),
    },
  },

  // Projects routes (not in main navigation - accessed via header dropdown)
  {
    path: "/projects",
    name: "Projects",
    component: () => import("@/pages/projects/ProjectsPage"),
    meta: {
      icon: "lucide:folder",
      translationKey: "projects.title",
      condition: () => hasPermission("projects:read"),
    },
  },
  {
    path: "/projects/:id/members",
    name: "ProjectMembers",
    component: () => import("@/pages/projects/ProjectMembersPage"),
    meta: {
      parent: "Projects",
      translationKey: "projects.members",
      condition: () => hasPermission("projects:manage_members"),
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "static-page",
          label: () => $t("projects.members"),
        },
      ],
    },
  },
  {
    path: "/settings",
    name: "Settings",
    redirect: "/settings/user",
    meta: {
      navigation: "dropdown",
      icon: "lucide:settings",
      translationKey: "settings",
    },
  },
  {
    path: "/settings/:tab",
    name: "Settings Tab",
    component: () => import("@/pages/settings/Settings"),

    meta: {
      icon: "lucide:settings",
      translationKey: "settings",
    },
  },
  {
    path: "/settings/numbers",
    redirect: { name: "OrganizationNumbers" },
  },
  {
    path: "/secrets",
    name: "Secrets",
    component: () => import("@/pages/secrets/SecretsPage"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: "lucide:lock-keyhole",
      translationKey: "secrets",
      condition: () => hasPermission("secrets:read"),
    },
  },
  {
    path: "/webhooks",
    name: "Webhooks",
    component: () => import("@/pages/webhooks/Webhook"),
    meta: {
      navigation: "menu",
      navigationGroup: "project",
      icon: "lucide:webhook",
      translationKey: "webhooks",
      condition: () => hasPermission("webhooks:read"),
    },
  },
  {
    path: "/webhooks/:id/event-logs",
    name: "Webhook Event Logs",
    component: () => import("@/pages/webhooks/WebhookEventLogs"),
    meta: {
      translationKey: "webhooks_event_logs",
      condition: () => hasPermission("webhooks:read"),
      parent: "Webhooks",
      projectSwitchProtection: true,
      breadcrumb: [
        {
          type: "entity-selector",
          label: createWebhookDropdownLabel,
        },
      ],
    },
  },
] as RouteRecordRaw[];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/oauth/authorize",
      name: "OAuthAuthorize",
      component: () => import("@/pages/oauth/OAuthAuthorize"),
      meta: { requiresAuth: true },
    },
    {
      path: "/oauth/device",
      name: "OAuthDevice",
      component: () => import("@/pages/oauth/OAuthDevice"),
    },
    {
      path: "/login",
      name: "Login",
      component: () => import("@/layouts/Login"),
    },
    {
      path: "/login/:okta_path",
      name: "Okta Login",
      component: () => import("@/layouts/Login"),
    },

    {
      path: "/register",
      name: "Register",
      component: () => import("@/layouts/Register"),
    },
    {
      path: "/signup/:access_code",
      name: "Signup",
      component: () => import("@/layouts/Signup"),
    },
    {
      path: "/pricing",
      name: "Pricing",
      component: () => import("@/layouts/pricing/Pricing"),
    },
    {
      path: "/invitations/:token/setup",
      name: "SetupAccount",
      component: () => import("@/pages/invitations/SetupAccount"),
    },
    {
      path: "/invitations/:token",
      name: "AcceptInvitation",
      component: () => import("@/pages/invitations/AcceptInvitation"),
    },
    {
      path: "/reactivate-account/:hash",
      name: "ReactivateAccount",
      component: () => import("@/layouts/ReactivateAccount"),
    },
    {
      path: "/shared/thread/:token",
      name: "Shared Thread",
      component: () => import("@/pages/shared/SharedThread"),
    },
    {
      path: "/",
      name: "Home",
      redirect: "/dashboard",
      component: () => import("@/layouts/Main"),
      meta: {
        requiresAuth: true,
      },
      children: routes,
    },
    // fallback that redirects to home
    {
      path: "/:pathMatch(.*)*",
      redirect: (to) => {
        if ((["/en", "/pt"] as const).some((path) => to.path.startsWith(path))) {
          nextTick(() => {
            setLocale(to.path.slice(1, 3) as "en" | "pt", { skipSync: true });
          });

          return {
            path: to.path.replace(/\/(en|pt)/, ""),
          };
        }

        return { name: "Home" };
      },
    },
  ],
});

let isInitialized = false;

router.beforeEach(async (to, _, next) => {
  const auth = useAuth();

  if (!isInitialized) {
    await auth.checkToken().catch(() => {});
    // await auth.getOktaCredentials();
    // const { host, pathname, protocol, port } = window.location;
    // const allowedHosts = [
    //   "localhost", // localhost com qualquer porta
    //   "dev.sippulse.ai", // dev.sippulse.ai
    //   "sippulse.ai", // sippulse.ai
    // ];

    // const isAllowedHost = allowedHosts.some((allowedHost) => host.startsWith(allowedHost));

    // if (!auth.oktaCredentials && !isAllowedHost) {
    //   const newHost =
    //     allowedHosts.find((allowedHost) => host.includes(allowedHost)) || "sippulse.ai";

    //   let href = `${protocol}//${newHost}`;
    //   if (port) href += `:${port}`;
    //   if (pathname) href += pathname;

    //   window.location.href = href;
    // }

    isInitialized = true;
  }

  if (to.name === "Login" && auth.token) {
    return next(getLoginDestination(to.query.redirect, null));
  }

  if (to.meta.requiresAuth && !auth.token) {
    // Modifique esta linha
    return next({ path: "/login", query: { redirect: to.fullPath } });
  }

  if (to.meta.condition) {
    if (!(await to.meta.condition())) {
      // Find the first accessible route to redirect to
      let fallback: (typeof routes)[number] | undefined;
      for (const r of routes) {
        if (r.meta?.navigation !== "menu") continue;
        if (r.meta?.condition && !(await r.meta.condition())) continue;
        fallback = r;
        break;
      }

      const fallbackPath = fallback
        ? typeof fallback.redirect === "string"
          ? fallback.redirect
          : fallback.path
        : null;

      // Prevent infinite loop: if no accessible route or already going there, go to login
      if (!fallbackPath || fallbackPath === to.path) {
        return next({ path: "/login" });
      }

      return next(fallbackPath);
    }
  }

  return next();
});

router.onError((error, to) => {
  const isChunkLoadError =
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Importing a module script failed") ||
    error.message?.includes("error loading dynamically imported module") ||
    error.name === "ChunkLoadError";

  if (isChunkLoadError) {
    const reloadKey = `chunk-reload:${to.fullPath}`;
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, "1");
      globalThis.location.assign(to.fullPath);
    }
  }
});

export default router;
